import {
	EventProperties,
	EventPropertiesProps,
	InspectionProperties,
	InspectionPropertiesProps,
	TaskProperties,
	TaskPropertiesProps,
} from '../InspectionProperties';
import {
	MenuItem,
	Property,
	PropertyCard,
	useIsFeatureEnabled,
} from '@innovyze/stylovyze';
import { extractValue, getAssetType } from '../../utils';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Divider } from '@mui/material';
import { useGlobalization } from '@Translations/useGlobalization';
import { SpatialProperty, FeatureItem } from '../../utils';
import * as assetSchema from '@innovyze/lib_asset_schema';
import {
	assetSchemaSelectorByType,
	getAssetSchemaAction,
} from '@innovyze/lib_asset_schema_store';
import { getFieldTypeMapping } from '../../../../redux/asset/selectors/getFieldTypeMapping';

const extractPanelProperties = (
	asset: assetSchema.AssetSchema,
): assetSchema.SingleProperty[] => {
	const properties = new Set<assetSchema.SingleProperty>();

	for (const property of asset.getProperties({ showOnMapPanel: true })) {
		if (property.type === 'List') {
			property.properties.forEach(property => {
				properties.add(property);
			});
		} else {
			properties.add(property);
		}
	}

	return Array.from(properties);
};

export interface AssetPropertiesProps {
	item: FeatureItem;
	viewDetails?: (
		id: string,
		layerId?: string,
		assetId?: string,
		assetType?: string,
	) => void;
	viewDetailsForAssetTypes?: string[];
	viewInspection?: InspectionPropertiesProps['viewInspection'];
	viewTask?: TaskPropertiesProps['viewTask'];
	viewEvent?: EventPropertiesProps['viewEvent'];
	showInspections?: boolean;
	traceFromAsset?: (assetId: string, dsTrace?: boolean) => void;
}

const getValue = (
	item: FeatureItem | FeatureItem['risk'],
	prop: string,
	type?: string,
	unit?: string,
): string | undefined => {
	const itemValue =
		(item?.segments as FeatureItem[])?.[0][prop] || item?.[prop];
	const value = extractValue(itemValue as SpatialProperty);
	if (value == null) return undefined;

	if (type === 'dateonlyUTC') {
		try {
			const dateISO = new Date(value).toISOString();
			return dateISO;
		} catch {
			return value;
		}
	} else {
		return value + (unit ? unit : '');
	}
};

const getType = (type?: string, unit?: string): Property['type'] => {
	if (unit) return 'unit';
	if (type) {
		if (type === 'string') return 'text';
		if (type === 'date') return 'dateonlyUTC';
		return type as Property['type'];
	}
	return 'text';
};

const useSystemTypeDisplayName = (systemType: string | undefined) => {
	const fallback = systemType || '-';
	const [displayName, setDisplayName] = React.useState<string | undefined>(
		fallback,
	);

	useEffect(() => {
		if (systemType) {
			assetSchema.getSystemType(systemType).then(resolved => {
				setDisplayName(resolved?.displayName ?? fallback);
			});
		}
	}, [systemType, fallback]);

	return displayName;
};

export const AssetProperties = ({
	item,
	viewDetails,
	viewInspection,
	viewTask,
	viewEvent,
	viewDetailsForAssetTypes,
	showInspections,
	traceFromAsset,
}: AssetPropertiesProps): JSX.Element => {
	const assetTrace = useIsFeatureEnabled('info-360-map-asset-trace');
	const { t } = useGlobalization();
	const dispatch = useDispatch();

	const showEvents = useIsFeatureEnabled('info-360-am-events');

	const assetType = getAssetType(item);
	const systemType = item.layerSystemType;

	useEffect(() => {
		if (systemType !== 'SpatialData') {
			dispatch(getAssetSchemaAction({ systemType, assetType }));
		}
	}, [dispatch, systemType, assetType]);

	const schema = useSelector(state => {
		if (assetType) {
			return assetSchemaSelectorByType(state, assetType);
		}
	});
	const effectiveSystemType = systemType || schema?.systemType.id;
	const systemTypeDisplayName = useSystemTypeDisplayName(effectiveSystemType);

	const assetProperties = schema ? extractPanelProperties(schema) : [];

	const showViewDetails =
		viewDetails &&
		(viewDetailsForAssetTypes?.includes(assetType) ||
			!viewDetailsForAssetTypes?.length);

	const panelProperties: Property[] = [
		{
			title: t('System Type', { context: 'Asset properties' }) ?? 'System Type',
			value: systemTypeDisplayName,
			ellipsis: true,
			loading: !effectiveSystemType,
			cy: 'systemType',
			span: 4,
		},
		{
			title: t('Asset Type', { context: 'Asset properties' }) ?? 'Asset Type',
			value: `${t(schema?.displayName ?? item.displayType ?? '-') ?? schema?.displayName ?? item.displayType ?? '-'}`,
			ellipsis: true,
			loading: !schema && !item.displayType,
			cy: 'assetType',
			span: 4,
		},
	];

	if (assetProperties.length === 0) {
		panelProperties.push({
			title: t('Asset Id', { context: 'Asset properties' }) ?? 'Asset Id',
			value: `${item.assetId}`,
			ellipsis: true,
			cy: 'assetId',
			span: 4,
		});
	} else {
		panelProperties.push(
			...assetProperties.map(property => {
				const { fieldType, precision } = getFieldTypeMapping(property);
				const value = getValue(
					item,
					property.id,
					fieldType,
					property.units,
				);

				return {
					title: t(property.displayName, {
						context: 'Asset property',
					}) ?? property.displayName,
					value,
					loading: !effectiveSystemType && !value,
					cy: property.id,
					type: getType(fieldType, property.units),
					span: 4,
					decimalPlaces: precision,
					ellipsis: true,
				};
			}),
		);
	}

	let menuItems: MenuItem[] = [];
	if (showViewDetails) {
		menuItems = [
			...menuItems,
			{
				text: t('View Details', {
					context: 'Asset properties menu item - view more details',
				}) ?? 'View Details',
				onClick: () =>
					viewDetails?.(
						item.id,
						item.layerId,
						item.assetId,
						item.assetType,
					),
			},
		];
	}
	if (assetTrace && (item.dsNodeId || item.usNodeId)) {
		menuItems = [
			...menuItems,
			{
				text: t('Upstream Selection', {
					context:
						'Asset properties menu item - perform upstream trace',
				}) ?? 'Upstream Selection',
				onClick: () => traceFromAsset?.(item.id),
			},
			{
				text: t('Downstream Selection', {
					context:
						'Asset properties menu item - perform downstream trace',
				}) ?? 'Downstream Selection',
				onClick: () => traceFromAsset?.(item.id, true),
			},
		];
	}

	return (
		<PropertyCard
			title={item.assetId || item.id}
			properties={panelProperties}
			menuItems={menuItems}
			columns={12}
			marginBottom
			additionalContent={
				showInspections && item.inspections?.length ? (
					<>
						<Divider />
						{item.inspections.map((inspection, i) => {
							if (inspection.task) {
								return (
									<TaskProperties
										key={i}
										inspection={inspection}
										viewTask={viewTask}
									/>
								);
							} else if (showEvents && inspection.event) {
								return (
									<EventProperties
										key={i}
										inspection={inspection}
										viewEvent={viewEvent}
									/>
								);
							} else {
								return (
									<InspectionProperties
										key={i}
										inspection={inspection}
										viewInspection={viewInspection}
									/>
								);
							}
						})}
					</>
				) : (
					undefined
				)
			}
		/>
	);
};
